import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { setActivity, setLanguage } from 'shared/config/store/reducers/appSlice';
import { AVAILABLE_LANGUAGES, Language } from 'shared/const/languages';
import { getLanguage } from 'shared/config/store/selectors/app';

type Messages = {
  [id: string]: string;
};

type LocaleDataLoader = () => Promise<{ default: Messages }>;

type Locales = {
  [key in Language]: LocaleDataLoader;
};

const locales: Locales = {
  bg: () => import('shared/libs/intl/translates/bg.json'),
  cz: () => import('shared/libs/intl/translates/cz.json'),
  de: () => import('shared/libs/intl/translates/de.json'),
  ee: () => import('shared/libs/intl/translates/ee.json'),
  en: () => import('shared/libs/intl/translates/en.json'),
  es: () => import('shared/libs/intl/translates/es.json'),
  fr: () => import('shared/libs/intl/translates/fr.json'),
  it: () => import('shared/libs/intl/translates/it.json'),
  lt: () => import('shared/libs/intl/translates/lt.json'),
  lv: () => import('shared/libs/intl/translates/lv.json'),
  nl: () => import('shared/libs/intl/translates/nl.json'),
  pl: () => import('shared/libs/intl/translates/pl.json'),
  ru: () => import('shared/libs/intl/translates/ru.json'),
};

const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const language = useSelector(getLanguage);
  const [messages, setMessages] = useState<Messages>({});
  const dispatch = useDispatch();

  const onSetLocale = (locale: Language) => {
    dispatch(setLanguage(locale));
  };

  const loadLocaleData = async (locale: Language) => {
    const isHaveLocale = Object.keys(locales).includes(locale);
    const en = AVAILABLE_LANGUAGES.EN.id;
    dispatch(setActivity(true));
    try {
      const defaultMessagesRes = await locales[en]();
      const defaultMessages = defaultMessagesRes.default;
      if (!isHaveLocale) {
        onSetLocale(en);
      }
      const localeMessagesRes = locale !== en ? await locales[locale]() : { default: {} };
      const localeMessages = localeMessagesRes.default;

      setMessages({
        ...defaultMessages,
        ...localeMessages,
      });
    } catch (error) {
      console.error(`Unable to load messages for locale ${locale}:`, error);
    } finally {
      dispatch(setActivity(false));
    }
  };

  useEffect(() => {
    void loadLocaleData(language);
  }, [language]);

  return (
    <IntlProvider locale={language} messages={messages} defaultLocale="en">
      {children}
    </IntlProvider>
  );
};

export default LanguageProvider;
